import { NavigateFunction } from '@starsoft/common/interfaces/navigate-function';
import { useCallback } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { useNavigate } from '../../useNavigate';
import { usePathname } from '../../usePathname';
import { useSearchParams } from '../../useSearchParams';

/**
 * Custom hook to manage modal navigation.
 * This hook provides functions to generate modal links and navigate to modals.
 *
 * @returns {Object} - An object containing functions to get modal links and navigate to modals.
 *
 * Example usage:
 * const { getModalLink, navigateToModal } = useModalNavigation();
 * const link = getModalLink('myModal', 'param1=value1');
 * navigateToModal('myModal', 'param1=value1');
 */
export function useModalNavigation() {
  // Hook to manage URL search parameters
  const searchParams: URLSearchParams = useSearchParams();
  // Hook to navigate between routes
  const navigate: NavigateFunction = useNavigate();
  // Hook to get the current pathname
  const pathname: string = usePathname();
  const store = useAppStore();
  const isAdminView = store?.getState?.()?.theme?.isAdminView;
  /**
   * Function to generate a link for a modal.
   * This function constructs a URL with the given modal key and optional parameters.
   *
   * @param {string} modal - The key representing the modal.
   * @param {string} [params] - Optional query parameters to include in the URL.
   * @returns {string} - The constructed URL for the modal.
   *
   * Example usage:
   * const link = getModalLink('myModal', 'param1=value1');
   */
  const getModalLink = useCallback(
    (modal: string, params?: string): string => {
      // Clone the current search parameters
      let updatedSearchParams = new URLSearchParams(searchParams);

      // Check if the pathname includes 'sports' and adjust the 'slug' parameter
      if (pathname.includes('sports')) {
        const slug = updatedSearchParams.get('slug');

        // Set 'slug' to 'live' if it's missing or incomplete
        if (!slug || slug.includes('...')) {
          updatedSearchParams.set('slug', 'live');
        }

        // If additional parameters are provided, ensure 'slug' is set
        if (params) {
          const paramsObj = new URLSearchParams(params);

          if (!paramsObj.has('slug')) {
            paramsObj.set('slug', 'live');
            params = paramsObj.toString();
          }
        } else {
          updatedSearchParams.set('slug', 'live');
        }
      }

      // Construct the current query parameters string
      const currentQueryParams: string = updatedSearchParams.toString()
        ? `?${updatedSearchParams.toString()}`
        : '';

      // Return the full URL with the modal key
      return `${pathname?.replace('[...slug]', searchParams.get('slug'))}${params ? `${params?.includes('?') ? params : `?${params}`}` : currentQueryParams}#${modal}`;
    },
    [pathname, searchParams],
  );

  /**
   * Function to navigate to a modal.
   * This function uses the generated modal link to navigate to the modal.
   *
   * @param {string} modal - The key representing the modal.
   * @param {string} [params] - Optional query parameters to include in the URL.
   *
   * Example usage:
   * navigateToModal('myModal', 'param1=value1');
   */
  const navigateToModal = useCallback(
    (modal: string, params?: string) => {
      if (isAdminView) {
        return;
      }
      // Generate the URL for the modal
      const url: string = getModalLink(modal, params);
      // Navigate to the generated URL
      navigate(url, { preventScrollReset: true });
    },
    [navigate, getModalLink, isAdminView],
  );

  // Return the functions to get modal links and navigate to modals
  return {
    getModalLink,
    navigateToModal,
  };
}
